import { useRenderNotifier } from '@wui/common-library'
import { ComputedRef, Ref } from 'vue'

import { useFilesUpload } from '@/features/chat-files-upload'
import { useMessageEdit } from '@/features/message-edit'

import { draftModel } from '@/entities/messages'
import { useRoom } from '@/entities/use-room'

import { useLocalization } from '@/shared/composables/localization'
import { useUserFeatures } from '@/shared/composables/user-features'
import { MAX_UPLOAD_FILES_COUNT } from '@/shared/composables/web-dav'
import { FeaturesNames } from '@/shared/types/settings'
import { Room } from '@/shared/types/store'
import { convertUploadingFiles } from '@/shared/utils/convert-uploading-files'
import { chatFileToImFile, enrichChatFilesWithMediaDimensions } from '@/shared/utils/files'

export const useFilesDropHandler = (
  room: Ref<Room> | ComputedRef<Room>,
  options: { sendImmediately?: boolean } = {},
) => {
  const { sendImmediately = false } = options
  const { hasFeature } = useUserFeatures()
  const { notifyError } = useRenderNotifier()
  const { files, uploadFiles } = useFilesUpload(room)
  const { t } = useLocalization()
  const { send } = useRoom()
  const { isEditing, uploadingFilesInEditingMessage, filesToEdit } = useMessageEdit()

  const dropHandler = async (e: DragEvent) => {
    if (e.dataTransfer?.files && room.value && hasFeature(FeaturesNames.WEBCLIENT_FILES)) {
      const newFiles = convertUploadingFiles(e.dataTransfer?.files, room.value.id)
      await enrichChatFilesWithMediaDimensions(newFiles)
      const totalCountOfFiles = sendImmediately
        ? newFiles.length
        : (isEditing.value ? (uploadingFilesInEditingMessage.value.length + (filesToEdit.value?.length ?? 0)) : files.value.length) + newFiles.length

      if (totalCountOfFiles >= MAX_UPLOAD_FILES_COUNT) {
        notifyError(t('common.maxFilesError'))
        return
      }

      if (!sendImmediately) {
        if (!isEditing.value) {
          draftModel.useDraftMessageStore().setDraftFiles(room.value.pServedUser, [...files.value, ...newFiles])
        } else {
          uploadingFilesInEditingMessage.value.push(...newFiles)
        }
      }

      try {
        await uploadFiles(newFiles, room.value.pServedUser)

        if (sendImmediately) {
          await send('', room.value, newFiles)
        } else if (isEditing.value) {
          filesToEdit.value = [...filesToEdit.value ?? [], ...newFiles.filter(f => uploadingFilesInEditingMessage.value.some(file => file.file === f.file)).map(chatFileToImFile)]

          uploadingFilesInEditingMessage.value = []
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('common.unknownError')
        notifyError(errorMessage)
      }
    }
  }

  return {
    dropHandler,
  }
}
